// =============================================================================
//  Copyright   : 2021 semantics GmbH
//  Date        : $Date: 2021-04-22 21:54:17 +0200 (Thu, 22 Apr 2021) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 118699 $
//  Purpose     : call upload apis
// =============================================================================
// ---Third-party imports
import {put, takeEvery, take, call, actionChannel} from 'redux-saga/effects'

// ---Framework imports
import {has, get, size, range} from 'ldash'
import Api from 'uix/api'
import log from 'uix/logging'
import {buffers} from 'uix/sagas'
import {Actions} from 'uix/registries'

// ---Local imports
import {
  uploadFile,
  setUploadState,
  runUploadImportOnIds,
  analyzeUploadImportOnIds,
  setUploadImportJob,
  setAnalyzedImport,
} from './actions'

// ---Globals
function* uploadChunk({number, id, bytes}) {
  const file = new Blob([bytes])
  let {data, error} = yield Api.post('/api/upload/send', {id, number, file})
  if (data) {
    return data.result
  }
  return false
}

function* uploadFileChunked({fileid, uploadid, bytes, chunksize}) {
  /* partition given bytes in chunks and upload them
   */
  let remaining = bytes.byteLength
  let num_chunks = Math.ceil(bytes.byteLength / chunksize)
  log.debug('number of chunks: %o', num_chunks)

  for (let i of range(num_chunks)) {
    yield put(setUploadState(fileid, 'upload chunk', {chunks: num_chunks, chunk: i + 1}))
    let chunk_bytes = bytes.slice(i * chunksize, (i + 1) * chunksize)
    let success = yield uploadChunk({number: i, id: uploadid, bytes: chunk_bytes})
    if (!success) {
      log.error('chunk upload %s:%s failed', fileid, i)
      return {success: false, error: {chunk: i}}
    }
  }
  return {success: true}
}

function* _uploadFile({payload}) {
  /* upload a file given by id+bytes
   */
  let {fileid, bytes} = payload
  let filesize = bytes.byteLength
  log.debug('process %o bytes', filesize)
  let {data, error} = yield Api.get('/api/upload/init', {size: filesize, fname: fileid})
  if (data) {
    let {id: uploadid, chunksize} = data.result
    if (payload.chunksize) {
      chunksize = payload.chunksize
    }
    yield put(setUploadState(fileid, 'upload start', {uploadid, size: filesize}))
    let {success, error} = yield uploadFileChunked({fileid, uploadid, bytes, chunksize})
    if (!success) {
      log.error('error during upload : %o', fileid)
      yield put(setUploadState(fileid, 'upload failed'))
    } else {
      let {data, error} = yield Api.get('/api/upload/close', {id: uploadid})
      if (data && data.result) {
        yield put(setUploadState(fileid, 'upload done'))
      } else {
        yield put(setUploadState(fileid, 'close failed'))
      }
    }
  } else {
    log.error('error during upload init: %o => %o', fileid, error)
    yield put(setUploadState(fileid, 'init failed'))
  }
}

function* watchUploadFile() {
  let chan = yield actionChannel('UPLOAD_FILE', buffers.expanding())
  while (true) {
    let action = yield take(chan)
    yield _uploadFile(action)
  }
}

export const sagas = {
  watchUploadFile: watchUploadFile(),
}
